import { useState, useEffect, useRef } from 'react'
import { useMediaQuery } from '@kyc-ui/react'
import { useMsal, useAccount } from '@azure/msal-react'
import { AccountInfo } from '@azure/msal-browser'
import { useSelector } from 'src/store'
import useTheme from 'src/hooks/common/useTheme'
import AppAPI from 'src/api/app'

export type TCustomer = { id: number; name: string }

// const mockCustomers: TCustomer[] = [{ id: 626, name: 'Mock Customer 626' }]

interface UseAPPReturn {
  apiUrl?: string
  error?: Error
  isMobile: boolean
  customers: TCustomer[]
  token: string
  setToken: (value: string) => void
}

export default function useApp(): UseAPPReturn {
  const theme = useTheme()
  const { accounts } = useMsal()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const error = useSelector((state) => state.app.error)
  const [customers, setCustomers] = useState<TCustomer[]>([])
  // Stable fallback: `accounts[0] || {}` would be a new object every render when logged out.
  const noAccountFilter = useRef<Record<string, never>>({}).current
  const account: AccountInfo | null = useAccount(accounts[0] ?? noAccountFilter)
  const [token, setToken] = useState('')

  useEffect(() => {
    if (!account || !token) {
      setCustomers([])
      return
    }

    let cancelled = false

    ;(async () => {
      const resp = await AppAPI.getSharedCustomers()
      const items: TCustomer[] = []
      Object.keys(resp).forEach((key) => {
        if (key !== '__method' && !isNaN(+key)) {
          items.push({
            id: +key,
            name: resp[+key]
          })
        }
      })
      if (!cancelled) {
        setCustomers(items.sort((a, b) => a.name.localeCompare(b.name)))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, account])

  return {
    error,
    isMobile,
    customers,
    token,
    setToken
  }
}
